import wretch from '@exodus/fetch/wretch'
import lodash from 'lodash'
import assert from 'minimalistic-assert'
import urljoin from 'url-join'

const INDEXER_URL = 'https://algo-api.a.exodus.io' // indexer
const NODE_URL = 'https://algo.a.exodus.io' // node
const FAUCET_URL = 'https://algo-faucet.a.exodus.io'
const API_KEY = 'bd5833a117efc9256eee8e3488f2eb430555f690be37805070b259f16fafb052'

const camelizeKeys = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((v) => camelizeKeys(v))
  }

  if (obj !== null && obj.constructor === Object) {
    return Object.fromEntries(
      Object.keys(obj).map((key) => [lodash.camelCase(key), camelizeKeys(obj[key])])
    )
  }

  return obj
}

// doc: https://developer.algorand.org/docs/reference/rest-apis/indexer/

export class Api {
  constructor(indexerUrl, nodeUrl, faucetUrl, apiKey) {
    this.setIndexer(indexerUrl)
    this.setNode(nodeUrl)
    this.setFaucet(faucetUrl)
    this.setApiKey(apiKey)
    this.tokens = {}
  }

  setIndexer(indexerUrl) {
    this.indexerUrl = indexerUrl || INDEXER_URL
  }

  setNode(nodeUrl) {
    this.nodeUrl = nodeUrl || NODE_URL
  }

  setFaucet(faucetUrl) {
    this.faucetUrl = faucetUrl || FAUCET_URL
  }

  setApiKey(apiKey) {
    this.apiKey = apiKey || API_KEY
  }

  setTokens(assets = {}) {
    const algoTokens = lodash.pickBy(assets, (asset) => asset.assetType === 'ALGORAND_TOKEN')
    this.tokens = lodash.mapKeys(algoTokens, (v) => v.assetIndex)
  }

  request(path, contentType = 'application/json') {
    return wretch(urljoin(this.indexerUrl, path)).headers({
      'Content-Type': contentType,
      'x-algo-api-token': this.apiKey,
      'User-Agent': 'exodus',
    })
  }

  nodeRequest(path, contentType = 'application/json') {
    return wretch(urljoin(this.nodeUrl, path)).headers({
      'Content-Type': contentType,
      'x-algo-api-token': this.apiKey,
      'User-Agent': 'exodus',
    })
  }

  faucetRequest(path) {
    return wretch(urljoin(this.faucetUrl, path)).headers({
      'x-algo-api-token': this.apiKey,
      'User-Agent': 'exodus',
    })
  }

  getAssetIndexFromName(name) {
    const assetTokens = this.tokens
    return lodash.get(lodash.find(assetTokens, { name }), 'assetIndex')
  }

  getAssetNameFromAssetIndex(assetIndex) {
    const assetTokens = this.tokens
    return lodash.get(lodash.find(assetTokens, { assetIndex }), 'name')
  }

  async getStatus() {
    return this.nodeRequest('v2/status')
      .get()
      .json((body) => camelizeKeys(body))
  }

  async getReserveBalance(address) {
    const minAmount = 1e5
    return this.nodeRequest(`v2/accounts/${encodeURIComponent(address)}`)
      .get()
      .error(404, () => minAmount) // account not found
      .json()
      .then((res = {}) => {
        return res['min-balance'] || minAmount
      })
  }

  async getAccountInfo(address) {
    const EMPTY_RESULT = {
      'amount-without-pending-rewards': 0,
      'pending-rewards': 0,
      rewards: 0,
      amount: 0,
    }
    return this.request(`v2/accounts/${address}`)
      .get()
      .error(404, () => camelizeKeys(EMPTY_RESULT)) // account not found
      .json()
      .then(({ account = EMPTY_RESULT }) => camelizeKeys(account))
  }

  async getPendingRewards(address) {
    const { pendingRewards = 0 } = await this.getAccountInfo(address)
    return pendingRewards
  }

  async getUnknownTokensList(address, accountInfo) {
    const { assets = [] } = accountInfo || (await this.getAccountInfo(address))
    assets.forEach((t) => {
      // eslint-disable-next-line @exodus/mutable/no-param-reassign-prop-only
      t.name = this.getAssetNameFromAssetIndex(t.assetId) || 'unknown'
      // eslint-disable-next-line @exodus/mutable/no-param-reassign-prop-only
      t.assetIndex = t.assetId // alias
    })
    return assets
  }

  async getBalance(address, accountInfo) {
    // get only confirmed balance
    let { amountWithoutPendingRewards = 0, assets = [] } =
      accountInfo || (await this.getAccountInfo(address))
    assets = assets.filter((token) => {
      // eslint-disable-next-line @exodus/mutable/no-param-reassign-prop-only
      token.name = this.getAssetNameFromAssetIndex(token.assetId)
      // eslint-disable-next-line @exodus/mutable/no-param-reassign-prop-only
      token.assetIndex = token.assetId // alias
      return !!token.name
    })

    const tokensBalance = lodash.keyBy(assets, 'name')
    return {
      balance: amountWithoutPendingRewards, // - unconfirmedBalance
      tokensBalance, // { <name> : { amount: ... } }
    }
  }

  async getUnconfirmedBalance(address) {
    const { 'total-transactions': totalTxns, 'top-transactions': transactions } =
      await this.nodeRequest(`v2/accounts/${encodeURIComponent(address)}/transactions/pending`)
        .get()
        .json()
    if (!totalTxns) return 0
    return transactions.reduce((acc, tx) => {
      const unconfirmed =
        tx.type === 'pay' && tx.from === address ? acc + tx.payment.amount + tx.fee : 0
      return unconfirmed - tx.fromrewards // reward got during tx
    }, 0)
  }

  async isWhitelisted(address, assetName) {
    // returns true if address opt-in and is not frozen (contract approved it)
    const assetIndex = this.getAssetIndexFromName(assetName)
    if (!assetIndex) throw new Error(`got unknown asset ${assetName}`)
    const { assets = [] } = await this.getAccountInfo(address)

    return assets.some((token) => {
      return token.assetId === assetIndex && !token.isFrozen
    })
  }

  async getTransactionById(txId, address) {
    assert(txId, 'txId is required')
    assert(address, 'address is required')
    const res = await this.request(`v2/transactions?txid=${txId}&address=${address}`)
      .get()
      .error(500, () => ({ transactions: [] })) // TX not found
      .json()

    const tx = res.transactions[0]
    const hasTx = res.transactions.length
    return hasTx
      ? camelizeKeys({
          ...tx,
          ...tx['payment-transaction'],
        })
      : null
  }

  async getPendingTransactionById(txId) {
    assert(txId, 'txId is required')
    return this.nodeRequest(`v2/transactions/pending/${encodeURIComponent(txId)}`)
      .get()
      .json((body) => camelizeKeys(body))
  }

  async getSuggestedParams() {
    const params = await this.nodeRequest('v2/transactions/params').get().json()
    return {
      minFee: params['min-fee'],
      lastRound: params['last-round'],
      genesisID: params['genesis-id'],
      genesishashb64: params['genesis-hash'],
    }
  }

  async getBlock(round) {
    return this.request(`v2/blocks/${round}`).get().json()
  }

  /**
   * Get transactions from an address
   */
  async getTransactions(address, options = {}) {
    const cursor =
      options.cursor instanceof Date ? options.cursor.toISOString() : options.cursor || '1970-01-01'

    const { transactions = [] } = await this.request('v2/transactions')
      .query({
        address,
        'after-time': cursor,
        'before-time': '2200-01-01', // use future date to avoid relying on device OS clock
      })
      .get()
      .error(502, () => ({ transactions: [] })) // empty result with fresh address
      .json()

    let newCursor = Date.parse(cursor)
    const txs = transactions
      .map((tx) => {
        const parsedTx = this.parseTransaction(tx, address)
        const timestamp = tx['round-time'] * 1e3
        newCursor = Math.max(timestamp, newCursor) // update cursor
        return parsedTx
      })
      .filter((tx) => tx !== null)

    return { transactions: txs, newCursor }
  }

  parseTransaction(tx, address) {
    const txType = tx['tx-type']

    const parsedTx = {}
    switch (txType) {
      case 'afrz':
        {
          // unfreeze tx
          parsedTx.isTokenTx = true
          parsedTx.amount = 0
          parsedTx.to = lodash.get(tx, 'asset-freeze-transaction.address')
          const assetIndex = lodash.get(tx, 'asset-freeze-transaction.asset-id', null)
          const isWhitelistedTx = !lodash.get(tx, 'asset-freeze-transaction.new-freeze-status')

          const assetName = this.getAssetNameFromAssetIndex(assetIndex)
          if (!assetName) return null // token not supported

          parsedTx.token = {
            assetIndex,
            name: assetName,
            isTokenOptIn: false,
            isWhitelistedTx,
          }
        }

        break
      case 'axfer':
        {
          // token transfer tx
          parsedTx.isTokenTx = true
          parsedTx.amount = lodash.get(tx, 'asset-transfer-transaction.amount')
          parsedTx.to = lodash.get(tx, 'asset-transfer-transaction.receiver')
          const assetIndex = lodash.get(tx, 'asset-transfer-transaction.asset-id', null)
          const isSelfSend = tx.sender === parsedTx.to

          const isReverted = tx.sender !== address && parsedTx.to !== address // clawback transaction

          const assetName = this.getAssetNameFromAssetIndex(assetIndex)
          if (!assetName) return null // token not supported

          parsedTx.token = {
            assetIndex,
            name: assetName,
            isTokenOptIn: isSelfSend && parsedTx.amount === 0, // token approval tx
            isWhitelistedTx: false,
            isReverted,
          }
        }

        break
      case 'pay':
        // algo transfer tx
        parsedTx.isTokenTx = false
        parsedTx.amount = lodash.get(tx, 'payment-transaction.amount')
        parsedTx.to = lodash.get(tx, 'payment-transaction.receiver')
        parsedTx.memo = lodash.get(tx, 'note', '')
        parsedTx.token = undefined
        break
      case 'appl':
        // contract interaction
        parsedTx.amount = lodash.get(tx, 'inner-txns[0].asset-transfer-transaction.amount', 0)
        parsedTx.to = lodash.get(tx, 'inner-txns[0].asset-transfer-transaction.receiver', address)
        const assetIndex = lodash.get(tx, 'inner-txns[0].asset-transfer-transaction.asset-id', null)
        parsedTx.isTokenTx = !!assetIndex
        parsedTx.unparsed = true

        if (assetIndex) {
          const assetName = this.getAssetNameFromAssetIndex(assetIndex)
          if (!assetName) return null // token not supported
          parsedTx.token = {
            assetIndex,
            name: assetName,
            isTokenOptIn: false,
            isWhitelistedTx: false,
          }
        }

        break
      default:
        return null
    }

    Object.assign(parsedTx, {
      type: txType, // pay, axfer, afrz
      id: tx.id,
      block: tx['confirmed-round'],
      timestamp: tx['round-time'] * 1e3,
      date: new Date(tx['round-time'] * 1e3),
      fee: tx.fee,
      from: tx.sender,
      toRewards: tx['receiver-rewards'],
      fromRewards: tx['sender-rewards'],
      confirmations: 1,
    })

    return parsedTx
  }

  async getAirdrop({ address, asset, rawTx, investorToken }) {
    return this.faucetRequest('account/algorand')
      .post({ address, asset, rawTx, investorToken })
      .json()
  }

  /**
   * {@link https://developer.algorand.org/docs/rest-apis/indexer/#get-v2assets}
   * @param assetId
   * @returns {Promise<{symbol: string, decimals: number, name: string}|undefined>}
   */
  async getTokenMetadata(assetId) {
    const { assets } = await this.request('/v2/assets').query({ 'asset-id': assetId }).get().json()

    if (assets.length === 0) return

    assert(assets.length === 1)

    const { decimals, name, 'unit-name': symbol, url } = assets[0].params
    return { decimals, name, symbol, url }
  }

  /**
   * Broadcast a raw signed transaction
   *
   *   await broadcastTransaction(rawTx)
   */
  broadcastTransaction = async (rawTx) => {
    const blob = Buffer.from(rawTx, 'hex') // return Uint8Array in chrome runtime | Buffer in Node.js
    const res = await this.nodeRequest('v2/transactions', 'application/x-binary')
      .body(blob)
      .post()
      .error(400, (err) => {
        throw new Error(err.text)
      })
      .json()

    if (res) {
      console.log(`tx sent!`, res.txId)
      return { txId: res.txId }
    }
  }
}

export const algorandApi = new Api()
